import { type ComponentPropsWithoutRef, type ElementType, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const paragraphVariants = cva('', {
  variants: {
    variant: {
      default: 'text-sm text-gray-700',
      label: 'text-sm font-medium text-gray-700',
      muted: 'text-xs text-gray-500',
      bodyMuted: 'text-sm text-gray-600',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface ParagraphProps
  extends ComponentPropsWithoutRef<'p'>, VariantProps<typeof paragraphVariants> {}

export const Paragraph = forwardRef<HTMLParagraphElement, ParagraphProps>(
  ({ className, variant, ...props }, ref) => (
    <p ref={ref} className={cn(paragraphVariants({ variant }), className)} {...props} />
  )
);
Paragraph.displayName = 'Paragraph';

const headingVariants = cva('', {
  variants: {
    variant: {
      page: 'text-2xl font-semibold text-gray-900',
      section: 'text-base font-semibold text-gray-900',
    },
  },
  defaultVariants: {
    variant: 'section',
  },
});

type HeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface HeadingProps
  extends
    Omit<ComponentPropsWithoutRef<HeadingElement>, 'as'>,
    VariantProps<typeof headingVariants> {
  as?: HeadingElement;
}

export function Heading({ as = 'h2', className, variant, ...props }: HeadingProps) {
  const Comp = as as ElementType;
  return <Comp className={cn(headingVariants({ variant }), className)} {...props} />;
}

const inlineVariants = cva('', {
  variants: {
    variant: {
      default: '',
      inputIconSlot: 'absolute inset-y-0 left-3 flex items-center pointer-events-none',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface InlineProps
  extends ComponentPropsWithoutRef<'span'>, VariantProps<typeof inlineVariants> {}

export const Inline = forwardRef<HTMLSpanElement, InlineProps>(
  ({ className, variant, ...props }, ref) => (
    <span ref={ref} className={cn(inlineVariants({ variant }), className)} {...props} />
  )
);
Inline.displayName = 'Inline';
